import { Link } from "react-router-dom";
import { Globe2, BookOpenCheck, CalendarCheck2, Quote } from "lucide-react";
import { RevealSection } from "@/components/common/RevealSection";
import { Button } from "@/components/ui/button";
import { benefits, heroImage, testimonials } from "@/content/siteData";

const featuredTestimonialNames = ["Tony Senanayake", "Astha Jain"];

const featuredTestimonials = testimonials.filter((item) => featuredTestimonialNames.includes(item.name));
const compactTestimonials = testimonials.filter((item) => !featuredTestimonialNames.includes(item.name));

export default function HomePage() {
  return (
    <div className="px-6 md:px-12 lg:px-24" data-testid="home-page">
      <RevealSection className="mx-auto grid w-full max-w-7xl gap-12 py-14 md:grid-cols-[1.1fr_0.9fr] md:py-20">
        <div className="space-y-7">
          <p className="handwritten-note text-2xl text-secondary" data-testid="home-note">
            Hindi with Prabha • New Delhi & Online Worldwide
          </p>
          <h1 className="text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="home-main-heading">
            Speak Hindi confidently, live India fully.
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base" data-testid="home-subheading">
            Learn practical spoken Hindi tailored to your role in India with Prabha — specialist trainer for diplomats,
            embassy families, corporate expats, and global professionals.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              asChild
              className="h-12 rounded-full bg-accent px-7 text-base font-bold text-accent-foreground transition-transform duration-300 hover:scale-105"
              data-testid="home-book-trial-cta"
            >
              <Link to="/contact">
                <CalendarCheck2 className="h-4 w-4" />
                Book Your Free Trial Class
              </Link>
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="home-trust-points">
            <p className="rounded-2xl border border-border bg-white p-4 text-sm text-foreground/90 soft-card">
              <Globe2 className="mb-2 h-4 w-4 text-primary" />
              Nearly 30 years of teaching global learners
            </p>
            <p className="rounded-2xl border border-border bg-white p-4 text-sm text-foreground/90 soft-card">
              <BookOpenCheck className="mb-2 h-4 w-4 text-primary" />
              30+ active expat students learning with Prabha
            </p>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-border bg-[#fff8db] p-3 soft-card" data-testid="home-hero-image-wrapper">
          <img
            src={heroImage}
            alt="Hindi to English alphabet learning chart"
            className="relative z-10 h-full w-full rounded-xl border border-[#f0e4b5] bg-[#fff9e8] object-contain p-1"
            data-testid="home-hero-image"
          />
        </div>
      </RevealSection>

      <RevealSection className="mx-auto w-full max-w-7xl py-12" delay={0.1}>
        <h2 className="text-base text-primary md:text-lg" data-testid="home-benefits-heading">
          Why expat learners choose Hindi with Prabha
        </h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {benefits.map((item) => (
            <article key={item.title} className="rounded-2xl border border-border bg-white p-8 soft-card" data-testid={`benefit-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className="text-2xl font-semibold text-primary">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{item.description}</p>
            </article>
          ))}
        </div>
      </RevealSection>

      <RevealSection className="mx-auto w-full max-w-7xl py-8" delay={0.12}>
        <div className="flex flex-wrap items-end justify-between gap-4" data-testid="home-testimonials-header">
          <div>
            <p className="text-base text-primary md:text-lg" data-testid="home-testimonials-label">
              Testimonials
            </p>
            <h2 className="mt-1 text-3xl font-semibold text-foreground sm:text-4xl" data-testid="home-testimonials-heading">
              Trusted by expat families and global learners
            </h2>
          </div>
          <Button asChild variant="outline" className="rounded-full border-primary text-primary" data-testid="home-testimonials-view-all-button">
            <Link to="/testimonials">Read all testimonials</Link>
          </Button>
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2" data-testid="home-testimonials-featured-grid">
          {featuredTestimonials.map((item) => (
            <article
              key={`featured-${item.name}`}
              className="rounded-2xl border border-border bg-white p-6 soft-card"
              data-testid={`home-featured-testimonial-${item.name.toLowerCase().replace(/\W+/g, "-")}`}
            >
              <div className="mb-3 flex items-center gap-3" data-testid={`home-featured-testimonial-header-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                {item.avatar ? (
                  <img
                    src={item.avatar}
                    alt={`${item.name} testimonial`}
                    className="h-11 w-11 rounded-full border border-border object-cover object-top"
                    data-testid={`home-featured-testimonial-avatar-${item.name.toLowerCase().replace(/\W+/g, "-")}`}
                  />
                ) : (
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-muted/40 text-xs font-semibold text-primary"
                    data-testid={`home-featured-testimonial-avatar-fallback-${item.name.toLowerCase().replace(/\W+/g, "-")}`}
                  >
                    {item.name
                      .split(" ")
                      .slice(0, 2)
                      .map((part) => part[0])
                      .join("")}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-primary" data-testid={`home-featured-testimonial-header-name-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                    {item.name}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid={`home-featured-testimonial-header-location-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                    {item.location}
                  </p>
                </div>
              </div>
              <Quote className="h-5 w-5 text-secondary" />
              <p className="mt-3 text-sm leading-relaxed text-foreground/90 md:text-base" data-testid={`home-featured-testimonial-feedback-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                “{item.feedback}”
              </p>
            </article>
          ))}
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-3" data-testid="home-testimonials-compact-grid">
          {compactTestimonials.map((item) => (
            <article
              key={`compact-${item.name}`}
              className="rounded-2xl border border-border bg-white p-5"
              data-testid={`home-compact-testimonial-${item.name.toLowerCase().replace(/\W+/g, "-")}`}
            >
              <p className="line-clamp-4 text-sm leading-relaxed text-foreground/80" data-testid={`home-compact-testimonial-feedback-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                “{item.feedback}”
              </p>
              <p className="mt-3 text-sm font-semibold text-primary" data-testid={`home-compact-testimonial-name-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                {item.name}
              </p>
              <p className="text-xs text-muted-foreground" data-testid={`home-compact-testimonial-location-${item.name.toLowerCase().replace(/\W+/g, "-")}`}>
                {item.location}
              </p>
            </article>
          ))}
        </div>
      </RevealSection>

    </div>
  );
}